/* ============================================================
   ct-pdp.js  |  Cosmetique Totale productpagina
   ------------------------------------------------------------
   Geladen met defer vanuit layout/theme.liquid (alleen template product).
   Bevat de gedragslaag van de CT-blokken uit snippets/ct-pdp-*.liquid:
     1. [data-ct-scroll-row]  pijlen voor een horizontale rij (upsell, cross-sell)
     2. [data-ct-video-row]   video-rij: afspelen, muis-slepen, eigen scrollbar
     3. [data-ct-accordion]   hoogte-animatie bij openen/sluiten
     4. [data-ct-klarna]      termijnbedrag bij variantwissel
     5. [data-ct-countdown]   aftelklok (selling fast)
     6. [data-ct-pdp-upsell]  upsell-cascade, zelfde bron als de cart drawer
     7. [data-ct-sticky]      sticky ATC-balk (wordt naar <body> verplaatst)
     8. [data-ct-expert-card] huidtherapeuten-kaart op de productafbeelding
   Geen afhankelijkheden. Initialiseert op DOMContentLoaded en opnieuw
   bij shopify:section:load (theme editor).
   ============================================================ */
(() => {
  'use strict';

  /* ---------- 1. Horizontale rij met pijlen ---------- */
  function initScrollRow(root) {
    if (root.dataset.ctInit) return;
    root.dataset.ctInit = '1';

    const track = root.querySelector('[data-ct-scroll-track]');
    const prev = root.querySelector('[data-ct-scroll-prev]');
    const next = root.querySelector('[data-ct-scroll-next]');
    if (!track || !prev || !next) return;

    const step = () => {
      const card = track.firstElementChild;
      if (!card) return 240;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
      return card.offsetWidth + gap;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  }

  /* ---------- 2. Video-rij ---------- */
  function initVideoRow(root) {
    if (root.dataset.ctInit) return;
    root.dataset.ctInit = '1';

    const track = root.querySelector('[data-ct-video-track]');
    const scrollbar = root.querySelector('[data-ct-video-scrollbar]');
    const thumb = root.querySelector('[data-ct-video-thumb]');
    if (!track) return;

    const cards = [...root.querySelectorAll('.ct-pdp-videos__card')];
    const videos = cards.map((c) => c.querySelector('video')).filter(Boolean);

    const setState = (video) => {
      const card = video.closest('.ct-pdp-videos__card');
      if (card) card.classList.toggle('is-playing', !video.paused);
    };
    const pauseOthers = (active) => {
      videos.forEach((v) => {
        if (v !== active && !v.paused) v.pause();
      });
    };
    const toggle = async (video) => {
      try {
        if (video.paused) {
          pauseOthers(video);
          await video.play();
        } else {
          video.pause();
        }
      } catch (e) {
        /* autoplay-policy of onderbroken play(): negeren */
      }
    };

    cards.forEach((card) => {
      const button = card.querySelector('[data-ct-video-play]');
      const video = card.querySelector('video');
      if (!video) return;
      if (button) button.addEventListener('click', () => toggle(video));
      video.addEventListener('click', () => toggle(video));
      ['play', 'pause', 'ended'].forEach((ev) => video.addEventListener(ev, () => setState(video)));
    });

    /* Muis-slepen op de track (touch is native) */
    let dragging = false;
    let dragged = false;
    let startX = 0;
    let startLeft = 0;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.target.closest('[data-ct-video-play]')) return;
      dragging = true;
      dragged = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(e.pointerId);
    });
    track.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) dragged = true;
      track.scrollLeft = startLeft - dx;
    });
    const stopDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      setTimeout(() => { dragged = false; }, 0);
    };
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) => track.addEventListener(ev, stopDrag));
    track.addEventListener('click', (e) => {
      if (!dragged) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    /* Eigen scrollbar */
    if (!scrollbar || !thumb) return;

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const barWidth = scrollbar.clientWidth;
      if (barWidth <= 0) return;
      scrollbar.classList.toggle('is-hidden', maxScroll <= 0);
      if (maxScroll <= 0) return;
      const thumbWidth = Math.max((track.clientWidth / track.scrollWidth) * barWidth, 60);
      thumb.style.width = `${thumbWidth}px`;
      const x = (track.scrollLeft / maxScroll) * (barWidth - thumbWidth);
      thumb.style.transform = `translateX(${x}px)`;
    };
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    let thumbDragging = false;
    let thumbStartX = 0;
    let thumbStartLeft = 0;
    thumb.addEventListener('pointerdown', (e) => {
      thumbDragging = true;
      thumbStartX = e.clientX;
      thumbStartLeft = track.scrollLeft;
      thumb.setPointerCapture(e.pointerId);
      thumb.classList.add('is-dragging');
    });
    thumb.addEventListener('pointermove', (e) => {
      if (!thumbDragging) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const maxThumbX = scrollbar.clientWidth - thumb.offsetWidth;
      if (maxThumbX <= 0) return;
      track.scrollLeft = thumbStartLeft + (e.clientX - thumbStartX) * (maxScroll / maxThumbX);
    });
    const stopThumb = () => { thumbDragging = false; thumb.classList.remove('is-dragging'); };
    ['pointerup', 'pointercancel'].forEach((ev) => thumb.addEventListener(ev, stopThumb));

    scrollbar.addEventListener('click', (e) => {
      if (e.target === thumb) return;
      const rect = scrollbar.getBoundingClientRect();
      const maxThumbX = rect.width - thumb.offsetWidth;
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxThumbX <= 0 || maxScroll <= 0) return;
      const x = Math.min(Math.max(e.clientX - rect.left - thumb.offsetWidth / 2, 0), maxThumbX);
      track.scrollTo({ left: (x / maxThumbX) * maxScroll, behavior: 'smooth' });
    });
  }


  /* ---------- 3. Accordion: hoogte-animatie ----------
     Native <details> blijft de basis (werkt zonder JS en met de
     zoekfunctie van de browser). Hier alleen de animatie: bij openen eerst
     open zetten en van 0 naar de echte hoogte, bij sluiten eerst naar 0 en
     dan pas open=false. .is-closing laat het plusje meteen terugdraaien. */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.addEventListener('click', (e) => {
    const summary = e.target.closest('[data-ct-accordion] > summary');
    if (!summary) return;
    const details = summary.parentElement;
    const body = details.querySelector('[data-ct-accordion-body]');
    if (!body || typeof body.animate !== 'function' || reduceMotion()) return;

    e.preventDefault();
    if (details._ctAnim) details._ctAnim.cancel();
    const timing = { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

    if (!details.open || details.classList.contains('is-closing')) {
      details.classList.remove('is-closing');
      details.open = true;
      const h = body.scrollHeight;
      details._ctAnim = body.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], timing);
      details._ctAnim.onfinish = () => { details._ctAnim = null; };
    } else {
      details.classList.add('is-closing');
      const h = body.offsetHeight;
      details._ctAnim = body.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], timing);
      details._ctAnim.onfinish = () => {
        details.open = false;
        details.classList.remove('is-closing');
        details._ctAnim = null;
      };
    }
  });

  /* ---------- helpers ---------- */
  const formatMoney = (cents) => {
    if (window.theme && theme.Currency && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  /* De product-component vuurt variantChange (theme.Variants) met
     detail.variant. Eén listener per sectie, doorgegeven aan de blokken. */
  function onVariant(root, fn) {
    const section = root.closest('product-component') || root.closest('[data-section-type="product"]');
    if (!section) return;
    section.addEventListener('variantChange', (e) => {
      if (e.detail && e.detail.variant) fn(e.detail.variant);
    });
  }

  /* ---------- 4. Klarna ---------- */
  function initKlarna(root) {
    if (root.dataset.ctInit) return;
    root.dataset.ctInit = '1';
    const min = parseInt(root.dataset.min, 10) || 0;
    const max = parseInt(root.dataset.max, 10) || Infinity;
    onVariant(root, (variant) => {
      const price = variant.price;
      root.classList.toggle('hide', !variant.available || price < min || price > max);
      const amount = root.querySelector('[data-ct-klarna-amount]');
      if (amount) amount.innerHTML = formatMoney(Math.floor(price / 3));
    });
  }

  /* ---------- 5. Aftelklok ---------- */
  function initCountdown(root) {
    if (root.dataset.ctInit) return;
    root.dataset.ctInit = '1';
    const end = parseInt(root.dataset.end, 10) * 1000;
    const clock = root.querySelector('[data-ct-countdown-clock]');
    if (!end || !clock) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = Math.max(0, end - Date.now());
      if (left <= 0) {
        root.remove();
        return false;
      }
      const s = Math.floor(left / 1000);
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60);
      clock.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s % 60);
      return true;
    };
    if (tick()) {
      const timer = setInterval(() => { if (!tick()) clearInterval(timer); }, 1000);
    }
  }

  /* ---------- 6. Upsell (zelfde cascade als de cart drawer) ----------
     Bronnen en volgorde zoals updateRecommendations() in cart-form.js:
     merkregel -> complementary -> related -> fallback. De sections leveren
     de drawer-kaarten (snippets/ct-cart-upsell-row.liquid); hier worden
     alleen de .ct-upsell-item-kaarten eruit gehaald. Per sessie gecachet. */
  const upsellCache = {};
  const fetchText = (url) => {
    if (!upsellCache[url]) {
      upsellCache[url] = fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '');
    }
    return upsellCache[url];
  };
  const withSlash = (base) => (base.charAt(base.length - 1) === '/' ? base : base + '/');

  function upsellItems(html, excludeId) {
    if (!html || html.indexOf('ct-upsell-item') === -1) return [];
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return [...tmp.querySelectorAll('.ct-upsell-item')].filter((el) => el.dataset.productId !== excludeId);
  }

  /* Merken afwisselen, te beginnen met een ander merk dan dit product
     (zelfde regel als _mixByVendor in cart-form.js). */
  function mixByVendor(items, ownVendor) {
    const groups = {};
    const order = [];
    items.forEach((el) => {
      const v = el.dataset.vendor || '';
      if (!groups[v]) { groups[v] = []; order.push(v); }
      groups[v].push(el);
    });
    if (order.length < 2) return items;
    const vendors = order.filter((v) => v !== ownVendor);
    if (groups[ownVendor]) vendors.push(ownVendor);
    const mixed = [];
    let left = items.length;
    while (left > 0) {
      vendors.forEach((v) => {
        const next = groups[v].shift();
        if (next) { mixed.push(next); left--; }
      });
    }
    return mixed;
  }

  function initUpsell(root) {
    if (root.dataset.ctUpsellInit) return;
    root.dataset.ctUpsellInit = '1';

    const pid = root.dataset.productId;
    const vendor = root.dataset.vendor || '';
    const limit = parseInt(root.dataset.limit, 10) || 4;
    const track = root.querySelector('[data-ct-scroll-track]');
    if (!pid || !track) return;

    const routes = (window.theme && theme.routes) || {};
    const recBase = routes.productRecommendations || '/recommendations/products';
    const colBase = withSlash(routes.collections || '/collections');
    const homeBase = withSlash(routes.home || '/');
    const rec = (intent) => recBase + '?section_id=ct-cart-recommendations&product_id=' + pid + '&limit=' + (limit + 2) + '&intent=' + intent;

    const show = (items) => {
      track.innerHTML = '';
      items.slice(0, limit).forEach((el) => track.appendChild(el));
      root.classList.remove('hide');
    };

    const rule = vendor ? fetchText(colBase + 'upsell-' + encodeURIComponent(vendor) + '?section_id=ct-cart-upsell-fallback') : Promise.resolve('');
    rule.then((html) => {
      const ruleItems = mixByVendor(upsellItems(html, pid), vendor);
      if (ruleItems.length) return show(ruleItems);
      return Promise.all([fetchText(rec('complementary')), fetchText(rec('related'))]).then(([comp, rel]) => {
        const items = upsellItems(comp, pid).length ? upsellItems(comp, pid) : upsellItems(rel, pid);
        if (items.length) return show(items);
        return fetchText(homeBase + '?section_id=ct-cart-upsell-fallback').then((fb) => {
          const fbItems = upsellItems(fb, pid);
          if (fbItems.length) show(fbItems);
        });
      });
    });

    /* Toevoegen: zelfde route als de ATC-knop (product-ajax-form.js), dus de
       drawer krijgt de verse cart-markup mee en opent. */
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ct-upsell-add]');
      if (!btn || btn.classList.contains('btn--loading')) return;
      e.preventDefault();
      btn.classList.add('btn--loading');
      fetch((routes.cartAdd || '/cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: btn.dataset.variantId, quantity: 1 }],
          sections: 'ct-cart-ajax',
          sections_url: window.location.pathname
        })
      })
        .then((r) => r.json())
        .then((data) => {
          btn.classList.remove('btn--loading');
          if (data.status === 422 || data.status === 'bad_request') {
            if (data.description) window.alert(data.description);
            return;
          }
          btn.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { product: data, addToCartBtn: btn, sections: data.sections || null }
          }));
        })
        .catch(() => btn.classList.remove('btn--loading'));
    });
  }

  /* ---------- 7. Sticky ATC-balk ----------
     Zichtbaar als de echte ATC-knop boven het venster uit is; weg zodra hij
     (deels) in beeld is of je er nog niet voorbij bent. */
  function initSticky(bar) {
    if (bar.dataset.ctInit) return;
    const section = bar.closest('product-component') || bar.parentElement;
    const mainBtn = section.querySelector('.product-single__form [data-add-to-cart]');
    const addBtn = bar.querySelector('[data-ct-sticky-add]');
    if (!mainBtn || !addBtn || !('IntersectionObserver' in window)) return;
    bar.dataset.ctInit = '1';

    /* Naar <body> verplaatsen. position: fixed rekent vanaf het dichtstbijzijnde
       voorouderelement met een transform/filter; staat zo'n element van het
       thema om de sectie heen, dan plakt de balk aan de sectie in plaats van
       aan het scherm (David, 30-9: "sticky werkt niet goed"). In <body> kan
       dat niet gebeuren. De paginabreedte gaat als variabele mee, en een
       eerdere kopie van dezelfde sectie (theme editor) wordt opgeruimd. */
    const sid = section.getAttribute('data-section-id') || '';
    document.querySelectorAll('body > .ct-pdp-sticky').forEach((old) => {
      if (old !== bar && old.dataset.sectionId === sid) {
        if (old._ctStickyDestroy) old._ctStickyDestroy(); else old.remove();
      }
    });
    bar.dataset.sectionId = sid;
    const maxW = getComputedStyle(section).getPropertyValue('--ctPdpMax').trim();
    if (maxW) bar.style.setProperty('--ctPdpMax', maxW);
    document.body.appendChild(bar);

    const setVisible = (on) => {
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
      document.documentElement.classList.toggle('ct-sticky-atc-on', on);
    };

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    });
    visibilityObserver.observe(mainBtn);

    addBtn.addEventListener('click', () => {
      if (mainBtn.disabled || mainBtn.classList.contains('btn--loading')) {
        mainBtn.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      mainBtn.click();
    });

    /* Prijs en beschikbaarheid volgen de echte prijs en knop. */
    const now = bar.querySelector('[data-ct-sticky-now]');
    const was = bar.querySelector('[data-ct-sticky-was]');
    const mainPrice = section.querySelector('.product-single__meta [data-product-price]');
    const mainWas = section.querySelector('.product-single__meta [data-compare-price]');
    const mainWrap = section.querySelector('.product-single__meta [data-product-price-wrap]');
    const sync = () => {
      if (mainPrice && now) now.innerHTML = mainPrice.innerHTML;
      if (was) {
        const hasWas = mainWas && mainWas.textContent.trim() && !(mainWrap && mainWrap.classList.contains('hide'));
        was.innerHTML = hasWas ? mainWas.innerHTML : '';
        was.classList.toggle('hide', !hasWas);
      }
      const loading = mainBtn.classList.contains('btn--loading');
      addBtn.disabled = mainBtn.disabled || loading;
      addBtn.classList.toggle('btn--loading', loading);
      addBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
      const mainText = mainBtn.querySelector('[data-add-to-cart-text]');
      addBtn.textContent = mainBtn.disabled && mainText ? mainText.textContent.trim() : addBtn.dataset.defaultLabel;
    };
    addBtn.dataset.defaultLabel = addBtn.textContent.trim();
    const mo = new MutationObserver(sync);
    [mainPrice, mainWas, mainWrap].filter(Boolean).forEach((el) => mo.observe(el, { childList: true, subtree: true, characterData: true, attributes: true }));
    mo.observe(mainBtn, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, subtree: true, characterData: true });
    sync();
    bar._ctStickyDestroy = () => {
      visibilityObserver.disconnect();
      mo.disconnect();
      setVisible(false);
      bar.remove();
    };
  }


  /* ---------- 8. Huidtherapeuten-kaart op de afbeelding ----------
     De kaart wordt in de productkolom gerenderd (hidden) en hier naar de
     hoofdafbeelding verplaatst: de ouder van [data-product-photos]
     (HYPOTHESE: in Expanse is dat .product__main-photos; visueel checken).
     Wegklikken bewaart de keuze per sessie; daarna staat er een verified-
     pilletje dat de kaart weer opent. */
  const EXPERT_KEY = 'ctExpertCardClosed';
  function initExpertCard(card) {
    if (card.dataset.ctInit) return;
    card.dataset.ctInit = '1';
    const section = card.closest('product-component');
    const photos = section && section.querySelector('[data-product-photos]');
    const anchor = photos && photos.parentElement;
    const panel = card.querySelector('[data-ct-expert-panel]');
    const pill = card.querySelector('[data-ct-expert-open]');
    const closeBtn = card.querySelector('[data-ct-expert-close]');
    if (!anchor || !panel || !pill) return;

    anchor.classList.add('ct-expert-anchor');
    anchor.appendChild(card);

    let closed = false;
    try { closed = sessionStorage.getItem(EXPERT_KEY) === '1'; } catch (e) { /* privémodus */ }

    const setClosed = (state) => {
      panel.hidden = state;
      pill.hidden = !state;
      card.classList.toggle('is-collapsed', state);
      try { sessionStorage.setItem(EXPERT_KEY, state ? '1' : '0'); } catch (e) { /* genegeerd */ }
    };
    setClosed(closed);

    card.hidden = false;
    const reveal = () => card.classList.add('is-in');
    if (reduceMotion()) reveal(); else setTimeout(reveal, 600);

    if (closeBtn) closeBtn.addEventListener('click', () => { setClosed(true); pill.focus(); });
    pill.addEventListener('click', () => { setClosed(false); if (closeBtn) closeBtn.focus(); });
  }

  function initPricedAtc(button) {
    if (button.dataset.ctPriceInit) return;
    const section = button.closest('.product-section');
    const price = section && section.querySelector('.product-single__meta [data-product-price]');
    const target = button.querySelector('[data-ct-atc-price]');
    if (!price || !target) return;
    button.dataset.ctPriceInit = '1';
    const sync = () => { target.textContent = (price.querySelector('[aria-hidden="true"]') || price).textContent.trim(); };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(price, { childList: true, subtree: true, characterData: true });
    button._ctPriceDestroy = () => observer.disconnect();
  }

  /* ---------- init ---------- */
  function init(scope) {
    (scope || document).querySelectorAll('[data-ct-priced-atc]').forEach(initPricedAtc);
    (scope || document).querySelectorAll('[data-ct-scroll-row]').forEach(initScrollRow);
    (scope || document).querySelectorAll('[data-ct-video-row]').forEach(initVideoRow);
    (scope || document).querySelectorAll('[data-ct-klarna]').forEach(initKlarna);
    (scope || document).querySelectorAll('[data-ct-countdown]').forEach(initCountdown);
    (scope || document).querySelectorAll('[data-ct-pdp-upsell]').forEach(initUpsell);
    (scope || document).querySelectorAll('[data-ct-sticky]').forEach(initSticky);
    (scope || document).querySelectorAll('[data-ct-expert-card]').forEach(initExpertCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-ct-priced-atc]').forEach((button) => { if (button._ctPriceDestroy) button._ctPriceDestroy(); });
    document.querySelectorAll('body > [data-ct-sticky]').forEach((bar) => {
      if (bar.dataset.sectionId === e.detail.sectionId && bar._ctStickyDestroy) bar._ctStickyDestroy();
    });
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
